import React, { Component } from 'react'

import { graphql } from '@apollo/client/react/hoc'
import { Table, Collapse, UncontrolledTooltip, Badge } from 'reactstrap'
import toTitleCase from '../Functions/toTitleCase'
import moment from 'moment'
import gql from 'graphql-tag'
import PrimaryListingContainer from "../Material/PrimaryListingContainer"
import { HasPermissionsQuery } from "../Functions/querys"
import { Link } from 'react-router-dom'
import RepAvatar from '../Material/RepAvatar'
import UpdateIncidentInfo from './Components/UpdateIncidentInfo'
import withApolloClient from '../withApolloClient'
import { useAdvancedFilters } from '../Filters/filterUrl'
import { combineFilters } from '../Filters/presetFilters'
import ProgressTrackingBar from '../Material/ProgressTrackingBar'
import AutomatedTasksList from './Shared/AutomatedTasksList'
import TransferIncidentDataModal from './IncidentModals/TransferIncidentDataModal'
import ChangeIncidentReviewStatus from './IncidentModals/ChangeIncidentReviewStatus'
import { ReactComponent as UnauthorizedDriver } from '../assets/images/icons/unauthorized-driver.svg'
import { ReactComponent as IncidentReview } from '../assets/images/icons/incident-review.svg'
import './Incidents.css'

// Every filter this list applies now travels in `filters` (FilterInput[]): the
// listing page's per-tab presets plus the user's advanced filters, and the
// related/duplicates tab's car / VIN / same-day lookups. Only search and
// ordering remain as their own arguments.
const AllIncidentsQuery = gql`query AllIncidentsQuery($orderBy: [String], $first: Int!, $cursor: String, $searchTerm: String, $filters: [FilterInput], $includeReviewMetadata: Boolean! = false) {
    allIncidents(orderBy: $orderBy, first: $first, after: $cursor, searchTerm: $searchTerm, filters: $filters) {
            edges {
                node {
                id
                pk
                metadata @include(if: $includeReviewMetadata)
                dateAdded
                incidentDate
                incidentTime
                notificationDate
                description
                reportedBy
                isArchived
                location
                carTotaled
                title
                source
                incidentType
                incidentState
                dmvPlate
                carOnHold
                ridesharePlatform
                ridesharePeriod
                ridesharePeriodSource
                responsibleParty
                status
                dataProgress
                missingDataFields
                hasPoliceReport
                hasDashcamVideos
                hasOtherParties
                hasLiabilityClaim
                hasSubroClaim
                isRecordOnly
                branch {
                    id
                    name
                    stateCode
                    customId
                    tenant {
                        id
                        customId
                    }
                }
                driver{
                    id
                    name
                    currentAgreement {
                        id
                    }
                }
                assignedTo {
                        edges {
                            node {
                            id
                            username
                            firstName
                            lastName
                        }
                    }
                }
                car {
                    id
                    pk
                    vin
                    year
                    carModel {
                        id
                        make
                        name
                    }
                }
            }
        }
        pageInfo {
            endCursor
            hasNextPage
            length
        }
    }
    ridesharePlatformChoices: __type(name: "IncidentsIncidentRidesharePlatformChoices") {
            enumValues {
            name
            description
        }
    }
    ridesharePeriodChoices: __type(name: "IncidentsIncidentRidesharePeriodChoices") {
            enumValues {
            name
            description
        }
    }
    ridesharePeriodSourceChoices: __type(name: "IncidentsIncidentRidesharePeriodSourceChoices") {
            enumValues {
            name
            description
        }
    }
    responsiblePartyChoices: __type(name: "IncidentsIncidentResponsiblePartyChoices") {
            enumValues {
            name
            description
        }
    }
    sourceChoices: __type(name: "IncidentsIncidentSourceChoices") {
            enumValues {
            name
            description
        }
    }
    statusChoices: __type(name: "IncidentsIncidentStatusChoices") {
            enumValues {
            name
            description
        }
    }
    reportedByChoices: __type(name: "IncidentsIncidentReportedByChoices") {
            enumValues {
            name
            description
        }
    }
    incidentTypeChoices: __type(name: "IncidentsIncidentIncidentTypeChoices") {
            enumValues {
            name
            description
        }
    }
    incidentNodeFieldDescriptions: __type(name: "IncidentNode") {
            fields {
            name
            description
        }
    }
}
`

const buildDeferredIncidentFieldsQuery = (incidentIds) => {
    if (!incidentIds || !incidentIds.length) {
        return null
    }

    const variableDefinitions = incidentIds.map((_, index) => `$incidentId${index}: ID!`).join(", ")
    const incidentSelections = incidentIds
        .map(
            (_, index) => `
        incident_${index}: incident(incidentId: $incidentId${index}) {
    id
    openTaskCount
            driver {
        id
        hasPendingInsurance
        hasIncompleteReturn
    }
    incidentpartySet(isArchived: false) {
                edges {
                    node {
                id
                hasUnauthorizedDrivers
            }
        }
    }
}
`
        )
        .join("\n")

    return gql`
        query DeferredIncidentFields(${variableDefinitions}) {
            ${incidentSelections}
}
`
}

class IncidentList extends Component {
    constructor(props) {
        super(props)
        this.state = {
            openModal: "",
            loading: false,
            error: "",
            openCollapse: null,
            deferredIncidentFields: {},
            deferredIncidentFieldsLoadingIds: [],
        }
        this.isMountedFlag = false
        this.deferredIncidentFieldsInFlight = false
    }
    componentDidMount() {
        this.isMountedFlag = true
        this.loadDeferredIncidentFields()
    }
    componentDidUpdate(prevProps) {
        const currentIncidentIds = this.getCurrentIncidentIds(this.props)
        const previousIncidentIds = this.getCurrentIncidentIds(prevProps)
        if (currentIncidentIds.join(",") !== previousIncidentIds.join(",")) {
            this.loadDeferredIncidentFields()
        }
    }
    componentWillUnmount() {
        this.isMountedFlag = false
    }
    toggleModal = (modalName) => {
        if (this.state.openModal === modalName) {
            this.setState({ openModal: "" })
        } else {
            this.setState({ openModal: modalName })
        }
    }
    toggleCollapse = (value) => {
        this.setState({ openCollapse: this.state.openCollapse === value ? null : value })
    }
    
    fetchMetaData = (metadata = null, name) => {
        if (!metadata) return null
        try {
            const parsedMetadata = typeof metadata === "string" ? JSON.parse(metadata) : metadata
            return parsedMetadata && parsedMetadata[name] ? parsedMetadata[name] : null
        } catch (error) {
            return null
        }
    }

    getCurrentIncidentIds = (props = this.props) => {
        return props.allIncidents?.edges?.map(({ node }) => node?.id).filter(Boolean) || []
    }
    updateSelected = (incidentObject) => {
        const selectedIncidents = this.props.selectedIncidents || []
        const nextSelectedIncidents = selectedIncidents.map(incident => incident.id).includes(incidentObject.id)
            ? selectedIncidents.filter(incident => incident.id !== incidentObject.id)
            : [...selectedIncidents, incidentObject]

        this.props.setSelectedIncidents(nextSelectedIncidents)
    }
    toggleSelectAll = () => {
        const visibleIncidents = this.props.allIncidents?.edges?.map(({ node }) => node).filter(Boolean) || []
        const visibleIncidentIds = visibleIncidents.map(incident => incident.id)
        const selectedIncidents = this.props.selectedIncidents || []
        const selectedIncidentIds = selectedIncidents.map(incident => incident.id)

        if (visibleIncidentIds.length > 0 && visibleIncidentIds.every(id => selectedIncidentIds.includes(id))) {
            this.props.setSelectedIncidents(selectedIncidents.filter(incident => !visibleIncidentIds.includes(incident.id)))
            return
        }

        const nextSelectedIncidents = [...selectedIncidents]
        visibleIncidents.forEach((incident) => {
            if (!selectedIncidentIds.includes(incident.id)) {
                nextSelectedIncidents.push(incident)
            }
        })
        this.props.setSelectedIncidents(nextSelectedIncidents)
    }
    loadDeferredIncidentFields = async () => {
        const incidentIds = this.getCurrentIncidentIds()
        const missingIncidentIds = incidentIds.filter((incidentId) => !this.state.deferredIncidentFields[incidentId])

        if (!missingIncidentIds.length || this.deferredIncidentFieldsInFlight || !this.props.apolloClient) {
            return
        }

        const query = buildDeferredIncidentFieldsQuery(missingIncidentIds)
        if (!query) {
            return
        }

        this.deferredIncidentFieldsInFlight = true
        this.setState({ deferredIncidentFieldsLoadingIds: missingIncidentIds })
        try {
            const variables = missingIncidentIds.reduce((acc, incidentId, index) => {
                acc[`incidentId${index}`] = incidentId
                return acc
            }, {})
            const { data } = await this.props.apolloClient.query({
                query,
                variables,
                fetchPolicy: "network-only",
            })

            if (!this.isMountedFlag) {
                return
            }

            const nextDeferredIncidentFields = { ...this.state.deferredIncidentFields }
            missingIncidentIds.forEach((incidentId, index) => {
                nextDeferredIncidentFields[incidentId] = data[`incident_${index}`]
            })

            this.setState({ deferredIncidentFields: nextDeferredIncidentFields, deferredIncidentFieldsLoadingIds: [] }, () => {
                const stillMissingIncidentIds = this.getCurrentIncidentIds().filter(
                    (incidentId) => !nextDeferredIncidentFields[incidentId]
                )
                if (stillMissingIncidentIds.length) {
                    this.loadDeferredIncidentFields()
                }
            })
        } catch (error) {
            if (this.isMountedFlag) {
                this.setState({ error: error.message || "Failed to load incident badges.", deferredIncidentFieldsLoadingIds: [] })
            }
        } finally {
            this.deferredIncidentFieldsInFlight = false
        }
    }

    render() {
        const fieldDescriptions = this.props.incidentNodeFieldDescriptions?.fields.reduce((acc, field) => {
            acc[field.name] = field.description
            return acc
        }, {})
        const visibleIncidents = this.props.allIncidents?.edges?.map(({ node }) => node).filter(Boolean) || []
        const selectedIncidentIds = (this.props.selectedIncidents || []).map(incident => incident.id)
        const allVisibleIncidentsSelected = visibleIncidents.length > 0 && visibleIncidents.every(incident => selectedIncidentIds.includes(incident.id))

        return (
            <PrimaryListingContainer listLoading={this.props.loading} loadMoreEntries={this.props.allIncidents && this.props.allIncidents.pageInfo.hasNextPage ? this.props.loadMoreEntries : null} totalFetchedRows={this.props.allIncidents && this.props.allIncidents.edges.length} pageInfo={this.props.allIncidents && this.props.allIncidents.pageInfo} refetchListQuery={this.props.refetchQuery}>
                <Table responsive>
                    <thead>
                        <tr>
                            <th onClick={this.toggleSelectAll} className="select-all-checkbox" title={allVisibleIncidentsSelected ? "Unselect visible incidents" : "Select visible incidents"}>
                                <i className={allVisibleIncidentsSelected ? "fa fa-check-square-o" : "fa fa-square-o unchecked"} aria-hidden="true"></i>
                            </th>
                            <th className='actions-th'>Actions</th>
                            {this.props.columnConfig?.filter(col => col?.preferenceSettings?.isVisible).map((column, index) => (
                                column.sortable ?
                                    <th key={column.id} onClick={() => this.props.updateOrder(column.id)} style={{ cursor: "pointer" }}>{this.props.orderBy.some(order => order.includes(column.id)) ? <i className={"fa " + (this.props.orderBy.includes(column.id) ? 'fa-sort-asc' : 'fa-sort-desc')}></i> : <i className='fa fa-sort'></i>}&nbsp;&nbsp;{column.name}</th> :
                                    <th key={column.id}>
                                        {column.name}
                                        {fieldDescriptions && fieldDescriptions.hasOwnProperty(column.id) && fieldDescriptions[column.id] && <>
                                            <i className="fa fa-info-circle ml-1" style={{ fontSize: "12px" }} id={"infoCircle" + column.id} />
                                            <UncontrolledTooltip target={"infoCircle" + column.id} placement="bottom">{fieldDescriptions[column.id]}</UncontrolledTooltip>
                                        </>
                                        }
                                    </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {this.props.allIncidents && this.props.allIncidents.edges && this.props.allIncidents.edges.length > 0 ? this.props.allIncidents.edges.map((incident, i) => {
                            const deferredFields = this.state.deferredIncidentFields[incident.node.id] || {}
                            const hasDeferredData = Boolean(this.state.deferredIncidentFields[incident.node.id])
                            const isDeferredLoading = this.state.deferredIncidentFieldsLoadingIds.includes(incident.node.id)
                            const hasUnauthorizedDrivers = deferredFields.incidentpartySet?.edges?.some(({ node }) => node?.hasUnauthorizedDrivers)
                            const isSelected = selectedIncidentIds.includes(incident.node.id)
                            const incidentReviews = this.fetchMetaData(incident.node.metadata, "duplicate_reviews") || {}
                            const parentIncidentId = String(this.props.transferTargetIncident?.pk || "")
                            const hasDuplicateReview = Array.isArray(incidentReviews.is_duplicate) && incidentReviews.is_duplicate.some(review => String(review.incident_id) === parentIncidentId)
                            const hasNotDuplicateReview = Array.isArray(incidentReviews.is_not_duplicate) && incidentReviews.is_not_duplicate.some(review => String(review.incident_id) === parentIncidentId)
                            const duplicateReviewStatus = hasDuplicateReview ? "Duplicate" : hasNotDuplicateReview ? "Not Duplicate" : null

                            return (
                                <React.Fragment key={incident.node.id}>
                                    <tr className={incident.node.isArchived ? "incident-row-archived" : undefined}>
                                        <td scope="row" className="listing-checkbox-wrapper">
                                            {!isSelected && <strong className="listing-counter">{i + 1}</strong>}
                                            <span className="listing-checkbox incident-listing-checkbox" onClick={() => this.updateSelected(incident.node)}>
                                                {isSelected ? <i className="fa fa-check-square-o" aria-hidden="true"></i> : <i className="fa fa-square-o unchecked" aria-hidden="true"></i>}
                                            </span>
                                        </td>
                                        <td className='actions-td'>
                                            <span className="action-item">
                                                {isDeferredLoading ? (
                                                    <i className="fa fa-spinner fa-spin text-muted" aria-hidden="true" />
                                                ) : !hasDeferredData ? (
                                                    <span className="null-val-color">--</span>
                                                ) : deferredFields.openTaskCount === 0 ? (
                                                    <p className="mb-0 task-completed">
                                                        <i onClick={() => this.toggleCollapse("openTaskList" + i)} className={this.state.openCollapse === "openTaskList" + i ? "fa fa-chevron-up" : "fa fa-check"} />
                                                    </p>
                                                ) : (
                                                    <>
                                                        {this.state.openCollapse === "openTaskList" + i ? (
                                                            <i style={{ color: "#007bff" }} onClick={() => this.toggleCollapse("openTaskList" + i)} className={"fa fa-chevron-up"} />
                                                        ) : (
                                                            <p className="mb-0 missing-tasks-count" id={"missingTasks" + i} onClick={() => this.toggleCollapse("openTaskList" + i)}>
                                                                {deferredFields.openTaskCount}
                                                                <UncontrolledTooltip target={"missingTasks" + i} placement="top">Pending Tasks</UncontrolledTooltip>
                                                            </p>
                                                        )}
                                                    </>
                                                )}
                                            </span>
                                            <span className="action-item">
                                                <Link to={`/incident/${incident.node.id} `}>
                                                    <i id={"incidentDetails" + i} className="fa fa-external-link" aria-hidden="true" />
                                                    <UncontrolledTooltip target={"incidentDetails" + i} placement="bottom">View Incident</UncontrolledTooltip>
                                                </Link>
                                            </span>
                                            {this.props.transferTargetIncident && this.props.transferTargetIncident.id !== incident.node.id && (
                                                <span className="action-item">
                                                    <i
                                                        id={"transferData" + i}
                                                        className="fa fa-random incident-transfer-icon"
                                                        aria-hidden="true"
                                                        onClick={() => this.toggleModal("transferData" + incident.node.id)}
                                                    />
                                                    <UncontrolledTooltip target={"transferData" + i} placement="bottom">Transfer Data</UncontrolledTooltip>
                                                    {this.state.openModal === "transferData" + incident.node.id &&
                                                        <TransferIncidentDataModal
                                                            open
                                                            fromIncident={{ id: incident.node.id, pk: incident.node.pk, title: incident.node.title, incidentDate: incident.node.incidentDate }}
                                                            toIncident={this.props.transferTargetIncident}
                                                            handleClose={() => this.toggleModal("transferData" + incident.node.id)}
                                                            refetchQuery={this.props.refetchQuery}
                                                        />
                                                    }
                                                </span>
                                            )}
                                            {this.props.showDuplicateReviewActions && this.props.transferTargetIncident?.id !== incident.node.id && <>
                                                <span className="action-item">
                                                    <IncidentReview
                                                        id={"changeIncidentReviewStatus" + i}
                                                        className="incident-review-status-icon cursor-pointer"
                                                        width={20}
                                                        height={20}
                                                        onClick={() => this.toggleModal("changeIncidentReviewStatus" + incident.node.id)}
                                                    />
                                                    <UncontrolledTooltip target={"changeIncidentReviewStatus" + i} placement="bottom">Change incident review status</UncontrolledTooltip>
                                                </span>
                                                {this.state.openModal === "changeIncidentReviewStatus" + incident.node.id && (
                                                    <ChangeIncidentReviewStatus
                                                        open
                                                        parentIncidentId={this.props.transferTargetIncident.id}
                                                        incidentId={incident.node.id}
                                                        currentStatus={duplicateReviewStatus}
                                                        handleClose={() => this.toggleModal("changeIncidentReviewStatus" + incident.node.id)}
                                                        refetchQuery={this.props.refetchQuery}
                                                    />
                                                )}
                                            </>}
                                        </td>
                                        {this.props.columnConfig?.filter(column => column?.preferenceSettings?.isVisible).map(column => <React.Fragment key={column.id}>
                                            {column.id === "id" && <td>{incident.node.dateAdded ? moment(incident.node.dateAdded).format("MM/DD/YYYY") : <span className="null-val-color">--</span>}</td>}
                                            {column.id === "data_progress" && <td id={"dataProgress" + incident.node.pk} className='cursor-pointer'>
                                                <ProgressTrackingBar progress={incident.node.dataProgress || 0} missingDataFields={incident.node.missingDataFields} />
                                            </td>}
                                            {column.id === "title" &&
                                                <td>
                                                    {incident.node.title}
                                                    {incident.node.isRecordOnly &&
                                                        <>
                                                            &nbsp;&nbsp;
                                                            <i style={{ color: "#17a2b8", fontSize: 15 }} id={"recordOnlyTooltip" + i} className='fa fa-file-text' aria-hidden="true" />
                                                            <UncontrolledTooltip target={"recordOnlyTooltip" + i} placement="top">Record-Only Incident</UncontrolledTooltip>
                                                        </>
                                                    }
                                                    {isDeferredLoading ? (
                                                        <>
                                                            &nbsp;&nbsp;
                                                            <i className="fa fa-spinner fa-spin text-muted" aria-hidden="true" />
                                                        </>
                                                    ) : hasUnauthorizedDrivers &&
                                                    <>
                                                        &nbsp;&nbsp;
                                                        <UnauthorizedDriver width={20} id={"unauthorizedDriverTooltip" + i} className="pb-1" />
                                                        <UncontrolledTooltip target={"unauthorizedDriverTooltip" + i} placement="top">Incident has un-authorized driver</UncontrolledTooltip>
                                                    </>
                                                    }
                                                </td>
                                            }
                                            {column.id === "assignedTo" && <td className="assignedto-cell">
                                                <span id={`assignedTo${incident.node.pk}`}>
                                                    {incident.node.assignedTo?.edges?.slice(0, 1).map(e => <RepAvatar key={e.node.id} rep={e.node} />)}
                                                    {incident.node.assignedTo?.edges?.length > 1 ? ` + ${incident.node.assignedTo?.edges?.length - 1} ` : ""}
                                                </span>
                                                {incident.node.assignedTo?.edges?.length > 1 && <UncontrolledTooltip target={`assignedTo${incident.node.pk}`} placement="left" style={{ backgroundColor: "#f2f2f2" }}>
                                                    {incident.node.assignedTo?.edges?.map(e => <RepAvatar key={e.node.id} rep={e.node} keepOpen />)}
                                                </UncontrolledTooltip>}

                                                &nbsp;&nbsp;<i id={`assignedToEdit${incident.node.pk}`} className="fa fa-lg fa-pencil cursor-pointer" onClick={() => this.toggleModal("assignedTo" + incident.node.id)} title="Edit" />
                                                {this.state.openModal === "assignedTo" + incident.node.id &&
                                                    <UpdateIncidentInfo
                                                        title="Edit Assigned To"
                                                        name="assignedToIds"
                                                        type="select"
                                                        open={this.state.openModal === "assignedTo" + incident.node.id}
                                                        value={incident.node.assignedTo?.edges?.map(e => e.node) || []}
                                                        target={`assignedToEdit${incident.node.pk}`}
                                                        handleClose={() => this.toggleModal("assignedTo" + incident.node.id)}
                                                        incidentId={incident.node.id}
                                                        refetchIncident={this.props.refetchQuery}
                                                    />
                                                }
                                            </td>}
                                            {column.id === "car__id" && <td>
                                                {incident.node.car ?
                                                    <Link color="secondary" to={'/car/' + incident.node.car.pk}>
                                                        {incident.node.car.year} {toTitleCase(incident.node.car.carModel.make || "--")} {toTitleCase(incident.node.car.carModel.name || "--")}
                                                    </Link>
                                                    : <span className='text-danger'>No car attached</span>}
                                            </td>}
                                            {column.id === "car_vin" && <td>{incident.node.car?.vin || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "incident_branch" && <td>{incident.node.branch?.name ? <span className="bos-badge bos-badge-blue">{incident.node.branch.name}</span> : <span className="null-val-color">--</span>}</td>}
                                            {column.id === "dmvPlate" && <td>{incident.node.dmvPlate || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "driver__id" && <td>
                                                {incident.node?.driver?.id ?
                                                    <Link color="secondary" to={'/driver/' + incident.node?.driver?.id}>
                                                        {incident.node?.driver?.name.trim() ? <span>{incident.node?.driver?.name}</span> : <span className='text-danger'>No Name Found</span>}
                                                    </Link> : <span className='text-danger'>No driver attached</span>
                                                }
                                                {incident.node?.driver?.currentAgreement &&
                                                    <>
                                                        &nbsp;&nbsp;<i className="fa fa-circle" aria-hidden="true" id={"currentAgreement" + incident.node.pk} style={{ color: "green" }}></i>
                                                        <UncontrolledTooltip target={"currentAgreement" + incident.node.pk} placement="top">Driver has an active agreement</UncontrolledTooltip>
                                                    </>
                                                }
                                                {isDeferredLoading ? <>
                                                    &nbsp;&nbsp;
                                                    <i className='fa fa-spinner fa-spin text-muted' aria-hidden="true" />
                                                </> : <>
                                                    {deferredFields.driver?.hasIncompleteReturn && <>
                                                        &nbsp;&nbsp;
                                                        <i className='fa fa-hourglass-half' aria-hidden="true" id={"incompleteReturn" + incident.node.pk} style={{ color: "red" }} />
                                                        <UncontrolledTooltip target={"incompleteReturn" + incident.node.pk} placement="top">Driver has an incomplete return</UncontrolledTooltip>
                                                    </>}
                                                    {deferredFields.driver?.hasPendingInsurance && <>
                                                        &nbsp;&nbsp;<i className='fa fa-shield text-danger' id={"pendingInsurance" + incident.node.pk} />
                                                        <UncontrolledTooltip target={"pendingInsurance" + incident.node.pk} placement="top">Pending Insurance</UncontrolledTooltip>
                                                    </>}
                                                </>
                                                }
                                            </td>}
                                            {column.id === "notificationDate" && <td>{incident.node.notificationDate ? moment(incident.node.notificationDate).format("MM/DD/YYYY") : <span className="null-val-color">--</span>}</td>}
                                            {column.id === "incidentDate" && <td>{incident.node.incidentDate ? moment(incident.node.incidentDate).format("MM/DD/YYYY") : <span className="null-val-color">--</span>}</td>}
                                            {column.id === "incidentTime" && <td>{incident.node.incidentTime ? moment(incident.node.incidentTime, "HH:mm:ss").format("hh:mm A") : <span className="null-val-color">--</span>}</td>}
                                            {column.id === "status" && <td>{this.props.statusChoices?.enumValues.find(item => item.name === incident.node.status)?.description || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "incidentType" && <td>{this.props.incidentTypeChoices?.enumValues.find(item => item.name === incident.node.incidentType)?.description || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "source" && <td>{this.props.sourceChoices?.enumValues.find(item => item.name === incident.node.source)?.description || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "responsibleParty" && <td>{this.props.responsiblePartyChoices?.enumValues.find(item => item.name === incident.node.responsibleParty)?.description || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "reportedBy" && <td>{this.props.reportedByChoices?.enumValues.find(item => item.name === incident.node.reportedBy)?.description || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "incidentState" && <td>{incident.node.incidentState || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "location" && <td>{incident.node.location || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "carOnHold" && <td>{incident.node.carOnHold ? "Yes" : "No"}</td>}
                                            {column.id === "ridesharePlatform" && <td>{this.props.ridesharePlatformChoices?.enumValues.find(item => item.name === incident.node.ridesharePlatform)?.description || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "ridesharePeriod" && <td>{this.props.ridesharePeriodChoices?.enumValues.find(item => item.name === incident.node.ridesharePeriod)?.description || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "ridesharePeriodSource" && <td>{this.props.ridesharePeriodSourceChoices?.enumValues.find(item => item.name === incident.node.ridesharePeriodSource)?.description || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "carTotaled" && <td>{incident.node.carTotaled || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "hasPoliceReport" && <td>{incident.node.hasPoliceReport || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "hasDashcamVideos" && <td>{incident.node.hasDashcamVideos || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "hasOtherParties" && <td>{incident.node.hasOtherParties || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "hasLiabilityClaim" && <td>{incident.node.hasLiabilityClaim || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "hasSubroClaim" && <td>{incident.node.hasSubroClaim || <span className="null-val-color">--</span>}</td>}
                                            {column.id === "isRecordOnly" && <td>{incident.node.isRecordOnly ? "Yes" : "No"}</td>}
                                            {column.id === "isArchived" && <td>{incident.node.isArchived ? "Yes" : "No"}</td>}
                                            {column.id === "duplicateReviewStatus" && <td>
                                                {duplicateReviewStatus ? (
                                                    <Badge color={hasDuplicateReview ? "danger" : "success"} className="p-2">
                                                        {duplicateReviewStatus}
                                                    </Badge>
                                                ) : <span className="null-val-color">--</span>}
                                            </td>}
                                        </React.Fragment>
                                        )}
                                    </tr>
                                    {
                                        this.state.openCollapse === "openTaskList" + i &&
                                        <tr>
                                            <td colSpan={8}>
                                                <Collapse isOpen={this.state.openCollapse === "openTaskList" + i}>
                                                    <AutomatedTasksList incidentId={incident.node?.id} />
                                                </Collapse>
                                            </td>
                                        </tr>
                                    }
                                </React.Fragment>
                            )
                        }) : !this.props.loading && <tr>
                            <td colSpan={10}>No incidents found!</td>
                        </tr>
                        }
                    </tbody>
                </Table >
            </PrimaryListingContainer >)
    }
}


const IncidentListWithData = graphql(HasPermissionsQuery, {
    options: () => ({
        variables: { userPermissions: [""] },
        fetchPolicy: "network-only",
        notifyOnNetworkStatusChange: true
    }),
    props: ({ data: { hasPermissions, loading, variables } }) => ({
        hasPermissions, loading, variables
    })
})((graphql(AllIncidentsQuery, {
    options: ({ searchTerm, orderBy, filters, showDuplicateReviewActions }) => ({
        variables: { first: 30, searchTerm, orderBy, filters, includeReviewMetadata: Boolean(showDuplicateReviewActions) },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only'
    }),
    props: ({ data: { loading, allIncidents, fetchMore, variables, ridesharePlatformChoices, ridesharePeriodChoices, ridesharePeriodSourceChoices, responsiblePartyChoices, sourceChoices, statusChoices, reportedByChoices, incidentTypeChoices, incidentNodeFieldDescriptions } }) => ({
        loading,
        allIncidents,
        variables,
        statusChoices,
        ridesharePlatformChoices,
        ridesharePeriodChoices,
        ridesharePeriodSourceChoices,
        responsiblePartyChoices,
        sourceChoices,
        reportedByChoices,
        incidentTypeChoices,
        incidentNodeFieldDescriptions,
        refetchQuery: () => {
            let currentLength = allIncidents && allIncidents.edges ? allIncidents.edges.length : 30
            return fetchMore({
                query: AllIncidentsQuery,
                variables: {
                    ...variables,
                    first: currentLength,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    const newEdges = fetchMoreResult.allIncidents.edges
                    const pageInfo = fetchMoreResult.allIncidents.pageInfo
                    return {
                        allIncidents: {
                            edges: [...newEdges],
                            pageInfo,
                            __typename: previousResult.allIncidents.__typename
                        },
                    }
                },
            })
        },
        loadMoreEntries: () => {
            return fetchMore({
                query: AllIncidentsQuery,
                variables: {
                    cursor: allIncidents.pageInfo.endCursor,
                    ...variables
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    const newEdges = fetchMoreResult.allIncidents.edges
                    const pageInfo = fetchMoreResult.allIncidents.pageInfo
                    return {
                        allIncidents: {
                            edges: [...previousResult.allIncidents.edges, ...newEdges],
                            pageInfo,
                            __typename: previousResult.allIncidents.__typename
                        },
                    }
                },
            })
        },
    })
})(withApolloClient(IncidentList))))

// Class components can't call hooks -- this wrapper reads the ?filter= URL state
// and feeds it to the Apollo HOC chain as a plain prop. The listing page also
// owns per-tab presets (not archived / assignment scope); combine both into the
// single `filters` variable. Callers without presets (RelatedIncidentsTab) send
// the URL filters alone.
export default function IncidentListWithAdvancedFilters(props) {
    const [advancedFilters] = useAdvancedFilters()
    return <IncidentListWithData {...props} filters={combineFilters(props.presets, advancedFilters)} />
}
